import { ImportItemSetLineInput } from '@warebee/shared/data-access-api-dto';
import { RawBuilder, sql } from 'kysely';
import { ItemUomOrientationConstraint } from '../../../../engine-model/src';
import { makeMappingError, MappingSchema } from '../mapping';

export function isBaseUomLine(line: ImportItemSetLineInput) {
  return line.uom && (!line.lowerUom || line.lowerUom == line.uom);
}

export const IS_BASE_UOM_LINE_SQL = sql`uom is not null and (lower_uom is null or lower_uom = '' or lower_uom = uom)`;

export const ITEM_SET_UOM_FIELDS: (keyof ImportItemSetLineInput)[] = [
  'uom',
  'lowerUom',
  'unitsPerLowestUom',
  'netWeight',
  'length',
  'width',
  'height',
  'volume',
  'ean',
  'upc',
  'maxStack',
  'orientationConstraint',
];

export const ITEM_SET_MAPPING_SCHEMA: MappingSchema<ImportItemSetLineInput> = {
  title: 'Item set schema',
  description:
    'Contains information about items and their UOMs (Units or measurement)',
  fields: [
    {
      name: 'consignee',
      type: 'string',
      title: 'Client (Consignee)',
      description:
        'Client (consignee) — Name of your company (can be generated) or your client (For 3PLs)',
    },
    {
      name: 'sku',
      type: 'string',
      title: 'Item',
      description: 'Item code (SKU)',
    },
    {
      name: 'skuGroup',
      type: 'string',
      optional: true,
      title: 'Item Group',
      description: 'Item Group name or group id',
    },
    {
      name: 'uom',
      type: 'string',
      optional: false,
      title: 'UOM',
      description:
        'UOM — Unit of Measure, such as Each/Piece, Inner, Case, Layer or Pallet',
    },
    {
      name: 'lowerUom',
      type: 'string',
      optional: true,
      title: 'Lowest UOM',
      description:
        'Lowest unit of measure of the item that stock balance is managed in ("Each"), Pallet > Case > Inner > Each',
    },
    {
      name: 'unitsPerLowestUom',
      type: 'integer',
      optional: true,
      title: 'Units Per Lowest UOM',
      description:
        'Quantity expressed in the smallest Unit of Measure (UOM). For instance, the number of individual items within a Case or Pallet.',
    },
    {
      name: 'netWeight',
      type: 'number',
      optional: true,
      measureType: 'weight',
      title: 'Weight',
      description: 'Weight of the UOM',
    },
    {
      name: 'length',
      type: 'number',
      optional: true,
      measureType: 'size',
      title: 'Length (Depth)',
      description: 'Length of the UOM',
    },
    {
      name: 'width',
      type: 'number',
      optional: true,
      measureType: 'size',
      title: 'Width',
      description: 'Width of the UOM',
    },
    {
      name: 'height',
      type: 'number',
      optional: true,
      measureType: 'size',
      title: 'Height',
      description: 'Height of the UOM',
    },
    {
      name: 'volume',
      type: 'number',
      optional: true,
      measureType: 'volume',
      title: 'Volume',
      description: 'Volume of the UOM',
    },
    {
      name: 'ean',
      type: 'string',
      optional: true,
      title: 'Scan Code: EAN',
      description:
        'EAN: The EAN is similar to the UPC but contains 13 digits, more common internationally. It includes a country code, manufacturer code, product code, and a check digit. An example of an EAN could be 5001234567890.',
    },
    {
      name: 'upc',
      type: 'string',
      optional: true,
      title: 'Scan Code: UPC',
      description:
        'UPC: The UPC is a 12-digit code primarily used in North America. It typically consists of a manufacturer code, a product code, and a check digit. For example, a UPC might look like 012345678905., or any other QR or Barcode Type',
    },
    {
      name: 'name',
      type: 'string',
      optional: true,
      title: 'Item Name',
      description: 'Item Name - Item Title',
      excludeFromAiPrompt: true,
    },

    {
      name: 'description',
      type: 'string',
      optional: true,
      title: 'Item Description',
      description: 'Item Description - Item description metadata',
    },

    {
      name: 'subGroup',
      type: 'string',
      optional: true,
      title: 'Item Sub-Group',
      description:
        'Item Sub Group, or any additional product category, or classification',
    },

    {
      name: 'transportClass',
      type: 'string',
      optional: true,
      title: 'Item Transport Class',
      description: 'Item Transport Class, can be used as Sort by in Policies',
    },
    {
      name: 'stockCategory',
      type: 'string',
      optional: true,
      title: 'Item Stock category',
      description:
        'Special stock indicator and number, can be used as Sort by in Policies',
    },
    {
      name: 'storageClass',
      type: 'string',
      optional: true,
      title: 'Storage class',
      description: 'Storage Class/type, Packing Group, DOT Code, etc.',
    },
    {
      name: 'pollutionClass',
      type: 'string',
      optional: true,
      title: 'Pollution class',
      description:
        'Hazardous Material Classifications or Water pollution class (Hazmat SKU Label)',
    },
    {
      name: 'maxStack',
      type: 'integer',
      optional: true,
      title: 'Max Stackibility',
      description: 'Max item Stackibility',
    },
    {
      name: 'orientationConstraint',
      optional: true,
      type: {
        enumValues: [
          ItemUomOrientationConstraint.NO_ROTATE,
          ItemUomOrientationConstraint.ROTATE_XY,
          ItemUomOrientationConstraint.ROTATE_ANY,
        ],
      },
      defaultValueResolver: {
        no_rotate: ItemUomOrientationConstraint.NO_ROTATE,
        rotate_xy: ItemUomOrientationConstraint.ROTATE_XY,
        rotate_any: ItemUomOrientationConstraint.ROTATE_ANY,
      },
    },
  ],
  createValidationQueries: (
    src: RawBuilder<any>,
    lineNumberRef: RawBuilder<any>,
  ) => [
    sql`
        select
          ${makeMappingError({
            code: sql.lit('BusinessLogicValidationError'),
            type: sql.lit('Invalid units number'),
            message: sql`'Invalid units number (' || cast(coalesce(units_per_lowest_uom, 1) as varchar) || ') for base UOM ' || consignee || '|' || sku || ' ' || uom || ' ' || coalesce(lower_uom, 'n/a')`,
            line: lineNumberRef,
          })}
        from ${src}
        where ${IS_BASE_UOM_LINE_SQL} and (units_per_lowest_uom is not null and units_per_lowest_uom != 1)
      `,
    sql`
        select
          ${makeMappingError({
            code: sql.val('BusinessLogicValidationError'),
            type: sql.val('Multiple base UOMs'),
            message: sql`'Multiple base uoms ' || array_join(array_agg(uom), ', ') || ' for item ' || consignee || '|' || sku`,
            lines: sql`array_agg(${lineNumberRef})`,
          })}
        from ${src}
        where ${IS_BASE_UOM_LINE_SQL}
        group by consignee, sku
        having count(1) > 1
      `,
  ],
  primaryKey: ['consignee', 'sku', 'uom'],
  appendable: false, // append would cause issues with base uom handling
};
